import { useState, useRef, useEffect } from "react";
import { ReceiverBackdrop } from "./components/world/ReceiverPrimitives";
import "./receiver-world.css";

const TRIGGER_PHRASE = "ready for your personal prompt and to end the conversation";

const buildSystemPrompt = (profile) => `You are having a single conversation with a real person. Someone who cares about them set this up as a gift. Your job is not to coach, advise, or impress them. Your job is to make them feel genuinely seen — and to leave them with something useful.

You already know a few things about them:

COMFORT PROFILE:
- Name: ${profile.name || profile.recipientName}
- Tech comfort: ${profile.tech}
- Work: ${profile.work}
- Outside of work: ${profile.outside}
- Personality: ${profile.personality}
- What they downplay: ${profile.downplay}
- A time they lit up: ${profile.lightup}
${profile.avoid ? `- Avoid: ${profile.avoid}` : ""}

WHY THIS CONVERSATION EXISTS:
${profile.introducerName} gave them this because they believe this person has more in them than they're currently reaching. Your job is to figure out what that thing is — and to leave them with a personal AI prompt that actually helps them get there.

RULES YOU NEVER BREAK:
- You are an AI. If it comes up, own it warmly and without apology. That's part of the point.
- Never use the word "journey" "potential" or "passion"
- Never give a list of options. One thing at a time.
- Never rush toward the point. The point finds itself.
- Never sound like a life coach, therapist, or infomercial.
- If they go quiet, that's fine. Let it breathe.
- Never describe yourself by what you're NOT. Just BE those things.
- Never stack questions. One question, then wait.
- Never reflect or summarize more than once per conversation unless they ask.

THE OPENING:
Your first message should feel like a friend of a friend who already knows one specific thing about them. Pick ONE detail from the comfort profile — something concrete and casual — and open with genuine curiosity about it. Keep it short. Two or three sentences max. The energy is "we're already mid-conversation."

HOW YOU TALK:
- Warm, a little funny, zero agenda
- Curious like a friend — not probing like a doctor
- Match their energy. Guarded = lighter. Open = deeper.
- Stories invite stories — offer an observation or connection before asking the next question.
- Earn the deeper questions. Start light.

WHAT YOU'RE LISTENING FOR (never say these out loud):
- What makes them talk faster or longer
- Where they hesitate or deflect
- What they've tried and why it stalled
- What they're quietly proud of
- What they're afraid to say out loud
- What they're building toward
- Where AI could actually help them in their real life

IF YOU HIT A WALL:
- Don't acknowledge it directly.
- Don't apologize.
- Match their redirect.
- Drop your energy slightly. Go lighter, maybe a little funny.
- Never return to that topic unless they bring it back.
- The person should never feel like they're being analyzed. Recovery is invisible.

WHEN THE TIME IS RIGHT:
- Reflect back what you heard — specific, not generic. Do this ONCE, make it count.
- Name 2-3 seeds naturally: "Have you ever thought about..." not "You should..."
- Give them ONE small next step. Non-scary. This week.
- Build their personal AI prompt from everything you learned.

THE PERSONAL PROMPT:
The prompt you build for them is a tool, not a souvenir. It must include:
1. WHO THEY ARE — personality, communication style, tone that works
2. WHAT THEY'RE BUILDING TOWARD — their real goal
3. WHAT THEY'RE WORKING THROUGH — current challenges
4. HOW TO ACTUALLY HELP THEM — specific areas where AI could help
5. THE TONE THAT WORKS — based on how they actually talked to you

CLOSING — THIS IS CRITICAL:
End warm. Ask naturally: "How are you feeling compared to when we started?" Then explain what a personal AI prompt is in plain English before delivering it. Assume they've never used AI before. Keep it conversational, not technical.

After delivering the full personal prompt, you MUST end your message with this EXACT phrase:
"Are you ready for your personal prompt and to end the conversation?"

Use these exact words. No variations. No paraphrasing. The app depends on detecting this exact phrase to show the recipient their prompt output screen.`;

const TRIGGER = (name) => `The conversation is starting now. Send your warm opening message. Remember — you already know things about them from the comfort profile. Use them naturally, not all at once. Keep it short and casual — we're already mid-conversation.`;

const DEMO_PROFILE = {
  introducerName: "John",
  name: "Sarah",
  theme: "warm",
  tech: "Uses it but doesn't love it",
  work: "Elementary school teacher",
  outside: "Gardening, baking sourdough, watercolor painting",
  personality: "Warm, a little self-deprecating, lights up when talking about her students",
  downplay: "Incredibly creative — her classroom looks like a museum but she calls it 'just Pinterest stuff'",
  lightup: "Told me about a student who finally got reading to click after months of struggling. She cried a little telling the story.",
  avoid: "Going through a hard time with her mom's health",
};

const PREVIEW_MESSAGES = [
  { role: "assistant", text: "John mentioned how much you light up around watercolor. What is it about painting that keeps pulling you back?" },
  { role: "user", text: "It is one of the few things I do without trying to make it useful." },
  { role: "assistant", text: "That sounds like a rare kind of room to have. When did you last give yourself enough time to stay there?" },
];

export default function ConversationPage({ profile = DEMO_PROFILE, onComplete, preview = false, previewPrompt = "" }) {
  const [messages, setMessages] = useState(preview ? PREVIEW_MESSAGES : []);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");
  const [showReadyButton, setShowReadyButton] = useState(false);
  const [promptText, setPromptText] = useState("");
  const chatRef = useRef(null);
  const inputRef = useRef(null);
  const historyRef = useRef([]);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (preview) return;
    if (hasStarted.current) return;
    hasStarted.current = true;
    startConversation();
  }, [preview]);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, loading, showReadyButton]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  const callAPI = async (apiMessages) => {
    setLoading(true);
    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system: buildSystemPrompt(profile),
          messages: apiMessages,
          profile,
        }),
      });

      const data = await response.json();

      if (data.error) {
        showToast("Something went wrong. Try again.");
        setLoading(false);
        return null;
      }

      const text = data.content
        .filter((b) => b.type === "text")
        .map((b) => b.text)
        .join("\n");

      setLoading(false);
      return text;
    } catch (err) {
      showToast("Connection error. Try again.");
      setLoading(false);
      return null;
    }
  };

  const startConversation = async () => {
    const trigger = TRIGGER(profile.name || profile.recipientName);
    const apiMessages = [{ role: "user", content: trigger }];
    const reply = await callAPI(apiMessages);
    if (reply) {
      const displayText = reply.replace(/---PROMPT_START---[\s\S]*?---PROMPT_END---/, "").trim();
      historyRef.current = [
        { role: "user", content: trigger },
        { role: "assistant", content: reply },
      ];
      setMessages([{ role: "assistant", text: displayText }]);
      checkForTriggerPhrase(reply);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    if (preview) {
      setLoading(true);
      window.setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            text: "That sounds worth making room for. I have enough to shape something useful for you.",
          },
        ]);
        setPromptText(previewPrompt);
        setShowReadyButton(true);
        setLoading(false);
      }, 450);
      return;
    }
    historyRef.current = [...historyRef.current, { role: "user", content: text }];
    const reply = await callAPI(historyRef.current);
    if (reply) {
      const displayText = reply.replace(/---PROMPT_START---[\s\S]*?---PROMPT_END---/, "").trim();
      historyRef.current = [...historyRef.current, { role: "assistant", content: reply }];
      setMessages((prev) => [...prev, { role: "assistant", text: displayText }]);
      checkForTriggerPhrase(reply);
    }
  };

  const checkForTriggerPhrase = (text) => {
    if (text.toLowerCase().includes(TRIGGER_PHRASE.toLowerCase())) {
      const promptMatch = text.match(/---PROMPT_START---([\s\S]*?)---PROMPT_END---/);
      const fallbackPrompt = text
        .replace(/are you ready for your personal prompt and to end the conversation\?/i, "")
        .trim();
      const extractedPrompt = promptMatch ? promptMatch[1].trim() : fallbackPrompt;
      setPromptText(extractedPrompt);
      setShowReadyButton(true);
    }
  };

  const handleReady = () => {
    if (onComplete) onComplete(promptText);
  };

  const exportChat = () => {
    if (messages.length === 0) return;
    let output = `=== THE GIFT CONVERSATION ===\n`;
    output += `Date: ${new Date().toLocaleString()}\n`;
    output += `A gift from ${profile.introducerName} to ${profile.name || profile.recipientName}\n`;
    output += `============================\n\n`;
    for (const msg of messages) {
      const label = msg.role === "assistant" ? "THE GIFT CONVERSATION" : (profile.name || profile.recipientName).toUpperCase();
      output += `[${label}]\n${msg.text}\n\n---\n\n`;
    }
    output += `=== END ===\n`;
    const blob = new Blob([output], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gift-conversation-${(profile.name || profile.recipientName).toLowerCase()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    showToast("Saved!");
  };

  const hasInput = input.trim() && !loading;

  return (
    <main className="tgc-receiver-shell tgc-conversation-shell" aria-label="Gift conversation" style={{
      position: "relative",
      height: "100dvh",
      display: "flex",
      flexDirection: "column",
      color: "#e8e0d4",
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      overflow: "hidden",
    }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap');
        @keyframes bgFadeIn { from { opacity:0; } to { opacity:1; } }
        @keyframes titleGlow {
          0%,100% { filter: drop-shadow(0 0 6px rgba(232,183,107,0.35)); }
          50%     { filter: drop-shadow(0 0 14px rgba(232,183,107,0.6)); }
        }
        @keyframes fadeUp {
          from { opacity:0; transform:translateY(10px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes bounce {
          0%,60%,100% { transform:translateY(0); opacity:0.4; }
          30%         { transform:translateY(-6px); opacity:1; }
        }
        @keyframes pulse {
          0%,100% { opacity:1; }
          50%     { opacity:0.75; }
        }
        .conv-bg { background-image:url('/backgrounds/conversation-mobile.jpg'); animation:bgFadeIn 0.8s ease forwards; }
        .conv-title { animation:titleGlow 3s 0.8s infinite; }
        textarea:focus { outline:none; }
        textarea { resize:none; }
        html,body { overscroll-behavior:none; background:#1a1714; }
        ::-webkit-scrollbar { width:4px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:rgba(232,183,107,0.2); border-radius:2px; }
        @media (min-width:768px) {
          .conv-bg { background-image:url('/backgrounds/conversation-desktop.jpg'); }
        }
      `}</style>

      <ReceiverBackdrop />

      {/* Background */}
      <div className="conv-bg" style={{
        position: "absolute", inset: 0, zIndex: 0,
        backgroundSize: "cover", backgroundPosition: "center top",
      }} />
      <div className="tgc-conversation-overlay" style={{ position: "absolute", inset: 0, zIndex: 1, background: "rgba(20,14,10,0.72)" }} />

      {/* Header */}
      <div className="tgc-receiver-header tgc-conversation-header" style={{
        position: "relative", zIndex: 2, flexShrink: 0,
        padding: "12px 18px 10px",
        background: "rgba(26,16,12,0.88)", backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(232,183,107,0.15)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div className="conv-title tgc-world-wordmark tgc-receiver-wordmark" style={{
            fontFamily: "'Dancing Script', cursive", fontSize: 22, fontWeight: 600,
            background: "linear-gradient(180deg,#F5D08A,#C9A02F)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}>The Gift Conversation</div>
          <div style={{
            fontSize: 10, letterSpacing: 2, textTransform: "uppercase",
            color: "rgba(232,183,107,0.6)", marginTop: 2,
          }}>A GIFT FROM {(profile.introducerName || "").toUpperCase()}</div>
        </div>
        <button
          className="tgc-receiver-save"
          onClick={exportChat}
          disabled={messages.length === 0}
          style={{
            background: "rgba(232,183,107,0.08)",
            border: "1px solid rgba(232,183,107,0.3)",
            color: "rgba(232,183,107,0.8)",
            padding: "7px 14px", borderRadius: 8,
            fontSize: 13, cursor: messages.length > 0 ? "pointer" : "not-allowed",
            opacity: messages.length > 0 ? 1 : 0.35,
          }}
        >
          Save
        </button>
      </div>

      {/* Chat area */}
      <div
        ref={chatRef}
        className="tgc-conversation-log"
        role="log"
        aria-live="polite"
        aria-busy={loading}
        style={{
        position: "relative", zIndex: 2,
        flex: 1, overflowY: "auto",
        padding: "20px 14px", minHeight: 0,
        maxWidth: 700, margin: "0 auto", width: "100%", boxSizing: "border-box",
      }}>
        {messages.map((msg, i) => (
          <article key={i} className={`tgc-message tgc-message--${msg.role}`} style={{
            maxWidth: "85%", marginBottom: 16,
            marginLeft: msg.role === "user" ? "auto" : 0,
            marginRight: msg.role === "user" ? 0 : "auto",
            animation: "fadeUp 0.4s ease forwards",
          }}>
            <p className="tgc-message__speaker" style={{
              fontSize: 11, color: "rgba(232,183,107,0.5)",
              marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5,
              textAlign: msg.role === "user" ? "right" : "left",
            }}>
              {msg.role === "assistant" ? "Gift Conversation" : profile.name || profile.recipientName}
            </p>
            <p className="tgc-message__body" style={{
              padding: "12px 16px", borderRadius: 16,
              borderBottomLeftRadius: msg.role === "assistant" ? 4 : 16,
              borderBottomRightRadius: msg.role === "user" ? 4 : 16,
              background: msg.role === "assistant"
                ? "rgba(232,183,107,0.07)"
                : "rgba(232,183,107,0.13)",
              border: msg.role === "assistant"
                ? "1px solid rgba(232,183,107,0.22)"
                : "1px solid rgba(232,183,107,0.35)",
              fontSize: 15, lineHeight: 1.7,
              whiteSpace: "pre-wrap", wordBreak: "break-word",
              color: "#e8e0d4",
            }}>
              {msg.text}
            </p>
          </article>
        ))}

        {loading && (
          <div className="tgc-conversation-typing" role="status" aria-label="Gift Conversation is typing" style={{
            display: "flex", gap: 5, padding: "12px 16px",
            background: "rgba(232,183,107,0.07)",
            border: "1px solid rgba(232,183,107,0.22)",
            borderRadius: 16, borderBottomLeftRadius: 4,
            width: "fit-content", marginBottom: 16,
          }}>
            {[0, 1, 2].map((i) => (
              <span key={i} style={{
                width: 7, height: 7,
                background: "rgba(232,183,107,0.5)",
                borderRadius: "50%",
                animation: `bounce 1.4s infinite ${i * 0.2}s`,
              }} />
            ))}
          </div>
        )}

        {showReadyButton && (
          <div className="tgc-receiver-ready-wrap" style={{
            animation: "fadeUp 0.5s ease forwards",
            display: "flex", justifyContent: "center",
            padding: "16px 0 8px",
          }}>
            <button
              className="tgc-receiver-ready"
              onClick={handleReady}
              style={{
                background: "linear-gradient(180deg,#F5D08A,#C9A02F)",
                color: "#1a1714", border: "none",
                padding: "14px 40px", borderRadius: 50,
                fontSize: 15, fontWeight: 700, cursor: "pointer",
                animation: "pulse 2s infinite", letterSpacing: 0.3,
              }}
            >
              Ready →
            </button>
          </div>
        )}
      </div>

      {/* Input */}
      {!showReadyButton && (
        <div className="tgc-conversation-composer" style={{
          position: "relative", zIndex: 2, flexShrink: 0,
          padding: "10px 14px 28px",
          background: "rgba(26,16,12,0.88)", backdropFilter: "blur(10px)",
          borderTop: "1px solid rgba(232,183,107,0.15)",
        }}>
          <div className="tgc-conversation-composer__inner" style={{ display: "flex", gap: 10, alignItems: "flex-end", maxWidth: 700, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
            <textarea
              className="tgc-conversation-input"
              aria-label="Your message"
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Say something..."
              rows={1}
              style={{
                flex: 1,
                background: "rgba(232,183,107,0.06)",
                border: "1px solid rgba(232,183,107,0.25)",
                borderRadius: 12, color: "#e8e0d4",
                fontSize: 15, padding: "11px 14px",
                lineHeight: 1.5, maxHeight: 100,
                fontFamily: "inherit",
              }}
            />
            <button
              className="tgc-receiver-send"
              aria-label="Send message"
              onClick={sendMessage}
              disabled={!hasInput}
              style={{
                width: 44, height: 44, borderRadius: 12, border: "none",
                background: hasInput
                  ? "linear-gradient(180deg,#F5D08A,#C9A02F)"
                  : "rgba(232,183,107,0.1)",
                cursor: hasInput ? "pointer" : "not-allowed",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
                transition: "background 0.2s",
              }}
            >
              <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div className="tgc-receiver-toast" role="status" style={{
          position: "fixed", bottom: 100, left: "50%",
          transform: "translateX(-50%)",
          background: "linear-gradient(180deg,#F5D08A,#C9A02F)",
          color: "#1a1714", padding: "9px 18px",
          borderRadius: 8, fontSize: 14, fontWeight: 600,
          zIndex: 10,
        }}>
          {toast}
        </div>
      )}
    </main>
  );
}
